import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import avatarLisa from "@/assets/avatar-lisa.png";
import avatarSam from "@/assets/avatar-sam.png";
import avatarNova from "@/assets/avatar-nova.png";

export type ActionKind = "application" | "accepted" | "message" | "cv_request" | "ai_suggestion";
export type ActionGroup = "overdue" | "today" | "earlier";

const RECRUITER_AVATAR: Record<string, string> = {
  lisa: avatarLisa,
  sam: avatarSam,
  nova: avatarNova,
};

export interface ActionItem {
  id: string;
  kind: ActionKind;
  name: string;
  avatarUrl?: string | null;
  jobTitle?: string;
  ts: number;
  group: ActionGroup;
  kindLabel: string;
  waitLabel: string;
  ctaLabel: string;
  href: string;
}


const LIMIT_PER_SOURCE = 200;
const AI_SUGGESTION_LIMIT = 500;
const HOUR = 3600 * 1000;
const OVERDUE_MS = 48 * HOUR;

function waitLabel(ts: number): string {
  const h = Math.floor((Date.now() - ts) / HOUR);
  if (h < 1) return "net binnen";
  if (h < 24) return `${h} uur geleden`;
  const d = Math.floor(h / 24);
  return d === 1 ? "1 dag geleden" : `${d} dagen geleden`;
}

function groupForTs(ts: number): ActionGroup {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (ts >= start.getTime()) return "today"; // alles van vandaag altijd bovenaan
  const age = Date.now() - ts;
  if (age > OVERDUE_MS) return "overdue";
  return "earlier";
}


const KIND_LABEL: Record<ActionKind, string> = {
  application: "Nieuwe sollicitatie",
  accepted: "Uitnodiging geaccepteerd",
  message: "Nieuw bericht",
  cv_request: "CV geüpload",
  ai_suggestion: "Voorstel",
};


const GROUP_WEIGHT: Record<ActionGroup, number> = {
  overdue: 0,
  today: 1,
  earlier: 1,
};

interface Params {
  companyId?: string | null;
  userId?: string;
  jobIds?: string[];
}

export interface ActionQueueCounts {
  active_jobs: number;
  apps_month: number;
  apps_today: number;
  apps_24h: number;
  unread_messages: number;
}

interface QueuePayload {
  applications: any[];
  anon_apps: any[];
  unread_messages: any[];
  accepted: any[];
  cv_ready: any[];
  ai_suggestions: any[];
  counts: ActionQueueCounts;
}

export interface ActionQueueResult {
  items: ActionItem[];
  counts: ActionQueueCounts;
}

const EMPTY_COUNTS: ActionQueueCounts = {
  active_jobs: 0,
  apps_month: 0,
  apps_today: 0,
  apps_24h: 0,
  unread_messages: 0,
};

/**
 * Takenlijst + Control Room-tellers in één serverzijde bevraging. Elke regel
 * vraagt een handeling van het bedrijf en verdwijnt zodra die is uitgevoerd.
 */
export const useActionQueueData = ({ companyId, userId }: Params) => {
  return useQuery<ActionQueueResult>({
    queryKey: ["action-queue", companyId, userId],
    queryFn: async () => {
      if (!userId || !companyId) return { items: [], counts: EMPTY_COUNTS };

      const { data, error } = await supabase.rpc("company_action_queue", {
        p_company_id: companyId,
      });
      if (error) throw error;

      const payload = ((data as unknown) as QueuePayload) || ({} as QueuePayload);
      const counts = payload.counts || EMPTY_COUNTS;

      const items: ActionItem[] = [];
      const push = (
        item: Omit<ActionItem, "group" | "waitLabel" | "kindLabel"> & { kindLabel?: string }
      ) =>
        items.push({
          ...item,
          group: groupForTs(item.ts),
          waitLabel: waitLabel(item.ts),
          kindLabel: item.kindLabel || KIND_LABEL[item.kind],
        });

      const cvCandidates = (payload.cv_ready || []).filter((m: any) => {
        if (!m.cv_shared_at) return false;
        const viewedAt = m.viewed_at ? new Date(m.viewed_at).getTime() : 0;
        return !viewedAt || viewedAt < new Date(m.cv_shared_at).getTime();
      });
      // Alleen matches met een openstaande CV-taak overslaan; een al bekeken CV
      // mag de sollicitatietaak niet laten verdwijnen.
      const cvMatchIdSet = new Set<string>(cvCandidates.map((m: any) => m.id));

      // Dubbele sollicitaties (zelfde kandidaat + zelfde vacature) tonen we één keer.
      const seenApplication = new Set<string>();
      (payload.applications || []).forEach((m: any) => {
        if (cvMatchIdSet.has(m.id)) return; // CV-taak dekt deze match al
        const dedupeKey = `${m.from_user_id}-${m.job_id}`;
        if (seenApplication.has(dedupeKey)) return;
        seenApplication.add(dedupeKey);
        push({
          id: `app-${m.id}`,
          kind: "application",
          name: m.name || "Kandidaat",
          avatarUrl: m.avatar_url || null,
          jobTitle: m.job_title || undefined,
          ts: new Date(m.created_at).getTime(),
          ctaLabel: "Beoordelen",
          href: `/bedrijf/matches/${m.id}`,
        });
      });

      (payload.anon_apps || []).forEach((a: any) => {
        push({
          id: `anon-${a.id}`,
          kind: "application",
          name: (a.name || "").trim().split(" ")[0] || "Sollicitant",
          jobTitle: a.job_title || undefined,
          ts: new Date(a.created_at).getTime(),
          ctaLabel: "Beoordelen",
          href: `/bedrijf/matches?anon=${a.id}`,
        });
      });

      // Geaccepteerde uitnodigingen blijven staan tot ze uit "Nieuw" zijn verplaatst.
      (payload.accepted || [])
        .filter(
          (m: any) =>
            !m.current_status ||
            ["new", "accepted", "interested"].includes(m.current_status)
        )
        .forEach((m: any) => {
          push({
            id: `acc-${m.id}`,
            kind: "accepted",
            name: m.name || "Kandidaat",
            avatarUrl: m.avatar_url || null,
            jobTitle: m.job_title || undefined,
            ts: new Date(m.interest_responded_at || m.created_at).getTime(),
            ctaLabel: "Opvolgen",
            href: `/bedrijf/matches/${m.id}`,
          });
        });

      // Eén taak per gesprek: meerdere ongelezen berichten worden samengevoegd.
      const convos = new Map<string, any[]>();
      (payload.unread_messages || []).forEach((m: any) => {
        const key = m.match_id || `sender-${m.sender_id}`;
        if (!convos.has(key)) convos.set(key, []);
        convos.get(key)!.push(m);
      });

      convos.forEach((rows, key) => {
        const oldest = rows.reduce((a, b) =>
          new Date(a.created_at).getTime() <= new Date(b.created_at).getTime() ? a : b
        );
        push({
          id: `msg-${key}`,
          kind: "message",
          kindLabel: rows.length > 1 ? `${rows.length} nieuwe berichten` : "Nieuw bericht",
          name: oldest.name || "Kandidaat",
          avatarUrl: oldest.avatar_url || null,
          jobTitle: oldest.job_title || undefined,
          ts: new Date(oldest.created_at).getTime(),
          ctaLabel: "Reageren",
          href: oldest.match_id
            ? `/bedrijf/berichten?match=${oldest.match_id}`
            : "/bedrijf/berichten",
        });
      });

      cvCandidates.forEach((m: any) => {
        push({
          id: `cv-${m.id}`,
          kind: "cv_request",
          name: m.name || "Kandidaat",
          avatarUrl: m.avatar_url || null,
          jobTitle: m.job_title || undefined,
          ts: new Date(m.cv_shared_at).getTime(),
          ctaLabel: "CV bekijken",
          href: `/bedrijf/matches/${m.id}`,
        });
      });

      (payload.ai_suggestions || []).forEach((s: any) => {
        const key = s.recruiter_key as string | undefined;
        const recruiterName = key
          ? key.charAt(0).toUpperCase() + key.slice(1)
          : "AI Recruiter";
        push({
          id: `ai-${s.id}`,
          kind: "ai_suggestion",
          kindLabel: s.candidate_name ? `Voorstel ${s.candidate_name}` : "Voorstel",
          name: recruiterName,
          avatarUrl: (key && RECRUITER_AVATAR[key]) || null,
          jobTitle: s.job_title || undefined,
          ts: new Date(s.sent_at || Date.now()).getTime(),
          ctaLabel: "Bekijken",
          href: key
            ? `/bedrijf/ai-recruiter/${key}${s.candidate_id ? `?kandidaat=${s.candidate_id}` : ""}`
            : "/bedrijf/ai-recruiter",
        });
      });

      // Strikt chronologisch: nieuwste eerst. Items >48u vallen daardoor
      // vanzelf onderaan in het aparte urgentieblok.
      items.sort((a, b) => {
        const r = (a.group === "overdue" ? 1 : 0) - (b.group === "overdue" ? 1 : 0);
        if (r !== 0) return r;
        return b.ts - a.ts;
      });



      return { items, counts };
    },
    enabled: !!userId && !!companyId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    placeholderData: (prev: ActionQueueResult | undefined) => prev,
  });
};

/** Compatibele wrapper: alleen de takenlijst. */
export const useActionQueue = (params: Params) => {
  const query = useActionQueueData(params);
  return { ...query, data: query.data?.items };
};
